<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import AppointmentsAPI from 'dashboard/api/appointments';
import StatusBadge from './StatusBadge.vue';

const props = defineProps({
  contactId: { type: [Number, String], required: true },
});

const store = useStore();
const { t, locale } = useI18n();
const items = ref([]);
const loading = ref(false);
const filter = ref('all'); // all | upcoming | past

async function load() {
  loading.value = true;
  try {
    const accountId = store.getters.getCurrentAccountId;
    const r = await AppointmentsAPI.contactHistory(accountId, props.contactId);
    items.value = r.data?.data || [];
  } catch {
    items.value = [];
  } finally {
    loading.value = false;
  }
}

const localeForIntl = computed(() =>
  (locale.value || 'en-US').replace('_', '-')
);

const filtered = computed(() => {
  const now = new Date();
  if (filter.value === 'upcoming')
    return items.value.filter(a => new Date(a.scheduled_at) > now);
  if (filter.value === 'past')
    return items.value.filter(a => new Date(a.scheduled_at) <= now);
  return items.value;
});

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat(localeForIntl.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso));
}

function formatPrice(cents, currency = 'BRL') {
  if (cents == null) return null;
  return new Intl.NumberFormat(localeForIntl.value, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

onMounted(load);
</script>

<template>
  <div class="border-t border-n-strong pt-4 mt-4 px-4">
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('APPOINTMENTS.HISTORY.TITLE') }}
      </h3>
      <div
        class="inline-flex rounded border border-n-strong overflow-hidden text-xs"
      >
        <button
          v-for="f in ['all', 'upcoming', 'past']"
          :key="f"
          type="button"
          class="px-2 py-1"
          :class="
            filter === f
              ? 'bg-n-blue-9 text-white'
              : 'hover:bg-n-alpha-1 text-n-slate-11'
          "
          @click="filter = f"
        >
          {{ t(`APPOINTMENTS.HISTORY.FILTER.${f.toUpperCase()}`) }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-xs text-n-slate-11">
      {{ t('APPOINTMENTS.HISTORY.LOADING') }}
    </div>

    <div
      v-else-if="filtered.length === 0"
      class="text-xs text-n-slate-11 italic"
    >
      {{ t('APPOINTMENTS.HISTORY.EMPTY') }}
    </div>

    <ul v-else class="space-y-2">
      <li
        v-for="a in filtered"
        :key="a.id"
        class="rounded-lg border border-n-strong p-3 text-sm bg-n-alpha-0"
      >
        <div class="flex items-start justify-between gap-2">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <StatusBadge :status="a.status" />
              <span class="font-medium text-n-slate-12 truncate">
                {{ a.service?.name }}
              </span>
            </div>
            <p class="text-xs text-n-slate-11">
              {{ formatDateTime(a.scheduled_at) }}
              <span v-if="a.professional">
                &middot; {{ a.professional.name }}
              </span>
              <span v-if="a.partner"> &middot; {{ a.partner.name }}</span>
            </p>
            <p v-if="a.cancellation_reason" class="text-xs text-n-ruby-11 mt-1">
              {{ t('APPOINTMENTS.HISTORY.CANCELLED_REASON') }}
              {{ a.cancellation_reason }}
            </p>
            <p v-if="a.notes" class="text-xs text-n-slate-11 mt-1 italic">
              {{ a.notes }}
            </p>
          </div>
          <span
            v-if="formatPrice(a.price_cents, a.currency)"
            class="text-xs text-n-slate-11"
          >
            {{ formatPrice(a.price_cents, a.currency) }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>
